import React from 'react'
import {
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  AxisDomain,
  TooltipProps,
  TickGeneratorFunction,
} from 'recharts'
import { NumberType } from '@api'
import { prettyNumber } from '@ui'

const noData = [
  { name: 'a', value: 10 },
  { name: 'b', value: 8 },
  { name: 'c', value: 6 },
  { name: 'd', value: 4 },
  { name: 'e', value: 2 },
]

interface IVHBarProps {
  data: any
  layout: 'vertical' | 'horizontal'
  width: number
  height: number
  xdata: { key?: string; label?: string }
  ydata: { key?: string; label?: string }
  tooltip: (props: TooltipProps) => React.ReactNode
  colors?: any
  onClick?: (a: any) => void
  percent?: boolean
  margin?: { top?: number; bottom?: number; right?: number; left?: number }
  pretty?: boolean
}

export default function VHBar({
  layout,
  data,
  width,
  height,
  xdata,
  ydata,
  colors,
  percent,
  tooltip,
  margin,
  pretty,
  onClick,
}: IVHBarProps) {
  const skeleton = !data || !data.length
  const DATA = skeleton ? noData : data

  const mainDataKey = layout !== 'vertical' ? ydata.key || '' : xdata.key || ''

  let maxLen = layout === 'vertical' && !skeleton ? getMaxLabelLength(DATA, ydata.key || '') : 0

  if (maxLen > 15) {
    maxLen = 15
  }

  let left = maxLen * 5

  if (left > width / 2) {
    maxLen = Math.round(width / (2 * 5))
    left = maxLen * 5
  }

  let xDomain: [AxisDomain, AxisDomain] | undefined
  if (percent) {
    xDomain = [0, 100]
  }

  return (
    <BarChart
      layout={layout}
      width={width}
      height={height}
      data={DATA}
      style={{ fontSize: 12 }}
      margin={{
        ...margin,
        left,
      }}
    >
      <defs>
        {pretty ? (
          <linearGradient id="fill">
            <stop offset="0" stopColor="#128aef" />
            <stop offset="1" stopColor="#128aef" />
          </linearGradient>
        ) : (
          <linearGradient
            id="fill"
            gradientTransform={layout !== 'vertical' ? 'rotate(90)' : undefined}
          >
            <stop offset="0" stopColor={layout !== 'vertical' ? '#128aef' : '#24c7a3'} />
            <stop offset="0.5" stopColor="#128aef" />
            <stop offset="1" stopColor={layout !== 'vertical' ? '#24c7a3' : '#128aef'} />
          </linearGradient>
        )}
      </defs>
      {!skeleton && (
        <CartesianGrid
          stroke="#d7dde1"
          vertical={layout === 'vertical'}
          horizontal={layout !== 'vertical'}
        />
      )}

      {xdata.key && (
        <XAxis
          dataKey={xdata.key}
          axisLine={false}
          tickLine={false}
          type={layout === 'vertical' ? 'number' : 'category'}
          tick={!skeleton && { fill: 'var(--grey1)' }}
          domain={xDomain}
          tickFormatter={
            !skeleton && layout === 'vertical'
              ? (d) =>
                  prettyNumber({
                    value: d,
                    numberType: NumberType.ROUNDED_NUMBER,
                  })
              : undefined
          }
          label={{
            value: xdata.label || xdata.key,
            position: 'insideBottom',
            fill: 'var(--grey1)',
            fontStyle: 'italic',
            offset: 0,
          }}
        />
      )}

      {ydata.key && (
        <YAxis
          dataKey={ydata.key}
          axisLine={false}
          tickLine={false}
          allowDecimals={false}
          type={layout === 'vertical' ? 'category' : 'number'}
          interval={0}
          tick={(props: TickGeneratorFunction) =>
            !skeleton ? <CustomTick maxLen={maxLen} {...props} /> : undefined
          }
          label={{
            value: ydata.label || ydata.key,
            angle: -90,
            position: 'insideLeft',
            fill: 'var(--grey1)',
            fontStyle: 'italic',
            offset: layout === 'vertical' ? -maxLen * 5 + 10 : 10,
          }}
        />
      )}

      {!skeleton && <Tooltip animationDuration={0} content={tooltip} />}

      {colors && !skeleton ? (
        <Bar
          style={{ cursor: onClick ? 'pointer' : 'default' }}
          layout={layout}
          dataKey={layout === 'vertical' ? xdata.key || '' : ydata.key || ''}
          maxBarSize={40}
          isAnimationActive
          animationDuration={300}
          onClick={(d) => onClick && onClick(d.payload)}
        >
          {DATA.map((d: any, i: any) => {
            return <Cell key={i} fill={d.color ? `#${d.color}` : 'url(#fill)'} />
          })}
        </Bar>
      ) : (
        <Bar
          style={{ cursor: skeleton || !onClick ? 'default' : 'pointer' }}
          layout={layout}
          dataKey={mainDataKey}
          fill={skeleton ? '#ddd' : 'url(#fill)'}
          maxBarSize={40}
          isAnimationActive={!skeleton}
          animationDuration={300}
          onClick={(d) => onClick && onClick(d)}
        />
      )}
    </BarChart>
  )
}

const getMaxLabelLength = (items = [], key: string) => {
  let l = 0

  items.forEach((item: any) => {
    const value = item[key] || ''
    if (value.length > l) {
      l = value.length
    }
  })

  return l
}

const CustomTick = ({
  x,
  y,
  payload,
  maxLen,
}: {
  x?: number
  y?: number
  stroke?: string
  payload?: any
  maxLen: number
}) => {
  const formattedValue = Number.isNaN(Number(payload.value))
    ? payload.value
    : prettyNumber({
        value: payload.value,
        numberType: NumberType.ROUNDED_NUMBER,
      })

  return (
    <g transform={`translate(${x},${y})`}>
      <text x={0} y={0} dy={4} textAnchor="end" fill="var(--grey1)">
        {formattedValue.length > maxLen + 1 && maxLen !== 0
          ? `${formattedValue.substr(0, maxLen)}...`
          : `${formattedValue}` || 'n/a'}
      </text>
    </g>
  )
}
